import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Plus, Edit, Trash2, Save, X, FileText, AlertCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { WhatsAppTemplate } from '@/types/whatsapp';

export function WhatsAppTemplates() {
  const [isLoading, setIsLoading] = useState(true);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WhatsAppTemplate | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    message: ''
  });

  const { toast } = useToast();

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await fetch('/api/admin/whatsapp/templates');
      const data = await response.json();
      
      if (data.success) {
        setTemplates(data.data);
      } else {
        toast({
          title: "Erro",
          description: "Não foi possível carregar os templates.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar templates.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const extractVariables = (message: string): string[] => {
    const regex = /\{\{([^}]+)\}\}/g;
    const variables: string[] = [];
    let match;

    while ((match = regex.exec(message)) !== null) {
      variables.push(match[1]);
    }

    return [...new Set(variables)];
  };

  const resetForm = () => {
    setFormData({
      name: '',
      message: ''
    });
    setEditingTemplate(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (template: WhatsAppTemplate) => {
    setFormData({
      name: template.name,
      message: template.message
    });
    setEditingTemplate(template);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const saveTemplate = async () => {
    if (!formData.name || !formData.message) {
      toast({
        title: "Erro",
        description: "Preencha todos os campos obrigatórios.",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);

    try {
      const url = editingTemplate 
        ? '/api/admin/whatsapp/templates'
        : '/api/admin/whatsapp/templates';
      
      const method = editingTemplate ? 'PUT' : 'POST';
      const payload = editingTemplate 
        ? { ...formData, id: editingTemplate.id }
        : formData;

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Sucesso",
          description: editingTemplate 
            ? "Template atualizado com sucesso!" 
            : "Template criado com sucesso!"
        });
        
        await loadTemplates();
        closeDialog();
      } else {
        toast({
          title: "Erro",
          description: data.error || "Erro ao salvar template.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao salvar template.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const deleteTemplate = async (template: WhatsAppTemplate) => {
    if (!confirm(`Tem certeza que deseja excluir o template "${template.name}"?`)) {
      return;
    }

    setIsDeleting(true);

    try {
      const response = await fetch(`/api/admin/whatsapp/templates?id=${template.id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Sucesso",
          description: "Template excluído com sucesso!"
        });
        await loadTemplates();
      } else {
        toast({
          title: "Erro",
          description: data.error || "Erro ao excluir template.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao excluir template.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const variables = extractVariables(formData.message);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>Templates de Mensagem</span>
              </CardTitle>
              <CardDescription>
                Crie e gerencie templates para mensagens recorrentes
              </CardDescription>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreateDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Template
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>
                    {editingTemplate ? 'Editar Template' : 'Novo Template'}
                  </DialogTitle>
                  <DialogDescription>
                    Crie templates com variáveis para personalizar mensagens.
                    Use {"{{variável}}"} para marcar onde os dados serão inseridos.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Ex: Boas-vindas, Lembrete de pagamento"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Mensagem *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Olá {{nome}}, seu pagamento de {{valor}} vence em {{data}}."
                      rows={6}
                    />
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>{formData.message.length} caracteres</span>
                      <span>Ex: {"{{nome}}"} será substituído pelo nome do contato</span>
                    </div>
                  </div>

                  {/* Variables Preview */}
                  {variables.length > 0 && (
                    <div className="space-y-2 p-4 bg-blue-50 rounded-lg">
                      <Label className="text-sm font-medium text-blue-800">
                        Variáveis detectadas:
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {variables.map(variable => (
                          <Badge key={variable} variant="outline" className="bg-blue-100 text-blue-800">
                            {"{{" + variable + "}}"}
                          </Badge>
                        ))}
                      </div>
                      <p className="text-xs text-blue-700 mt-2">
                        Essas variáveis poderão ser preenchidas ao enviar a mensagem.
                      </p>
                    </div>
                  )}

                  {/* Examples */}
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Exemplos de uso:</strong>
                      <ul className="mt-2 space-y-1 text-sm">
                        <li>• {"{{nome}}"} - Nome do contato</li>
                        <li>• {"{{empresa}}"} - Nome da empresa</li>
                        <li>• {"{{valor}}"} - Valor a pagar</li>
                        <li>• {"{{data}}"} - Data de vencimento</li>
                      </ul>
                    </AlertDescription>
                  </Alert>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={closeDialog}>
                    <X className="h-4 w-4 mr-2" />
                    Cancelar
                  </Button>
                  <Button onClick={saveTemplate} disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {editingTemplate ? 'Atualizar' : 'Criar'} Template
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                Nenhum template encontrado
              </h3>
              <p className="text-foreground mb-4">
                Crie seu primeiro template para começar a enviar mensagens personalizadas.
              </p>
              <Button onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Primeiro Template
              </Button>
            </div>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Mensagem</TableHead>
                    <TableHead>Variáveis</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((template) => {
                    const templateVariables = extractVariables(template.message);
                    return (
                      <TableRow key={template.id}>
                        <TableCell>
                          <div className="font-medium">{template.name}</div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs">
                            <p className="text-sm truncate" title={template.message}>
                              {template.message}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {templateVariables.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {templateVariables.map(variable => (
                                <Badge key={variable} variant="outline" className="text-xs">
                                  {variable}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">Nenhuma</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {format(new Date(template.created_at!), "dd/MM/yyyy", { locale: ptBR })}
                            <div className="text-xs text-muted-foreground">
                              {format(new Date(template.created_at!), "HH:mm", { locale: ptBR })}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(template)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteTemplate(template)}
                              disabled={isDeleting}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Dicas de Uso</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-medium">Como usar variáveis:</h4>
              <ul className="text-sm text-foreground space-y-1">
                <li>• Use {"{{variável}}"} no lugar de dados que mudam</li>
                <li>• No envio individual, preencha cada variável</li>
                <li>• No envio em lote, {"{{nome}}"} é substituído automaticamente</li>
                <li>• Variáveis são opcionais - use sem conforme necessário</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium">Boas práticas:</h4>
              <ul className="text-sm text-foreground space-y-1">
                <li>• Crie nomes descritivos para seus templates</li>
                <li>• Mantenha mensagens claras e objetivas</li>
                <li>• Teste templates antes de usar em massa</li>
                <li>• Personalize sempre que possível</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
